import {DateUtil} from "../../../../utils/date.util";

export class TimeUtility {

  static getDuration(date1: Date, date2: Date): number {
    return Math.abs(date1.getTime() - date2.getTime())
  }

  static addMinutes(date: Date, minutes: number): Date {
    return new Date(date.getTime() + (minutes * 60 * 1000));
  }

  static addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + (days * 24 * 60 * 60 * 1000));
  }

  static addMonths(date: Date, months: number): Date {
    return new Date(date.setMonth(date.getMonth() + months));
  }

  static addYears(date: Date, years: number): Date {
    return new Date(date.setFullYear(date.getFullYear() + years));
  }

 static weekRange(date: Date): { start: Date, end: Date } {
    let day = date.getDay() || 7;
    if (day !== 1)
      date.setHours(-24 * (day - 1));
    return {
      start: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
      end: new Date(date.getFullYear(), date.getMonth(), date.getDate() + 6)
    }
 }

 static last7DaysRange(date: Date): { start: Date, end: Date } {
    return {
      start: this.addDays(date, -7),
      end: date
    }
 }

 static monthRange(date: Date): { start: Date, end: Date } {
    return {
      start: new Date(date.getFullYear(), date.getMonth(), 1),
      end: new Date(date.getFullYear(), date.getMonth() + 1, 0)
    }
 }

  static yearRange(date: Date): { start: Date, end: Date } {
    return {
      start: new Date(date.getFullYear(), 0, 1),
      end: new Date(date.getFullYear(), 11, 31)
    }
  }

  static msToTime(millis: number): { h: string, m: string, s: string, ms: string } {
    let milliseconds = Math.floor((millis % 1000) / 100),
      seconds = Math.floor((millis / 1000) % 60),
      minutes = Math.floor((millis / (1000 * 60)) % 60),
      hours = Math.floor((millis / (1000 * 60 * 60)) % 24);

    let h = (hours < 10) ? "0" + hours : hours + "";
    let m = (minutes < 10) ? "0" + minutes : minutes + "";
    let s = (seconds < 10) ? "0" + seconds : seconds + "";

    return {
      h: h,
      m: m,
      s: s,
      ms: milliseconds + ""
    }
  }

  static msToReadableTime(millis: number, showSeconds: boolean): string {
    let t = this.msToTime(millis)
    if (t.h === '00' && t.m === '00') return 'just now'
    let time = ''
    if (t.h !== '00')
      time = t.h + ' h '
    time = time + '' + t.m + ' min'

    if (showSeconds) time = ' ' + time + ' sec'
    return time + ' ago'
  }

  static calculateRange(period: string): { fromDate: Date, toDate: Date } | null {
    switch (period) {
      case 'TODAY': {
        let date = new Date()
        return {fromDate: this.getDayStart(date), toDate: this.getDayEnd(date)}
      }
      case 'YESTERDAY': {
        let date = this.getDayBefore(new Date(), 1)
        return {fromDate: this.getDayStart(date), toDate: this.getDayEnd(date)}
      }
      case 'THIS_WEEK': {
        let earlierDate = this.getDayBefore(new Date(), 7)
        return {fromDate: this.getDayStart(earlierDate), toDate: this.getDayEnd(new Date())}
      }

      case 'THIS_MONTH': {
        let dates = this.getFirstAndLastDayOfMonth(new Date())
        return {fromDate: dates.firstDay, toDate: dates.lastDay}
      }

      case 'LAST_MONTH': {
        let date = new Date()
        date.setMonth(date.getMonth() - 1) // same day last month
        let dates = this.getFirstAndLastDayOfMonth(date)
        return {fromDate: dates.firstDay, toDate: dates.lastDay}
      }

    }

    return null
  }

  static getDayBefore(date: Date, days: number): Date {
    return new Date(date.getTime() - (days * 24 * 60 * 60 * 1000));
  }

  static getDayStart(date: Date): Date {
    date.setHours(0, 0, 0, 0);
    return date
  }

  static getDayEnd(date: Date): Date {
    date.setHours(23, 59, 59, 999);
    return date
  }

  static getFirstAndLastDayOfMonth(date: Date): { firstDay: Date, lastDay: Date } {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {firstDay: this.getDayStart(firstDay), lastDay: this.getDayEnd(lastDay)}
  }

  static dateFromMonth(month: string): Date {
    const monthIndex = months.map(m => m.value)
      .findIndex(name => name.toLowerCase() === month.toLowerCase());

    const date = new Date();
    date.setUTCMonth(monthIndex !== -1 ? monthIndex : new Date().getMonth());
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0, 0);
    return date;
  }

  static toISO(paymentDateTime: Date): Date {
    let isoString = DateUtil.getIsoFormatDate(paymentDateTime)
    return new Date(isoString)
  }

  static toDateTimeLocal(date: Date): Date {
    let isoString = TimeUtility.formatToDateTimeLocal(date)
    return new Date(isoString)
  }

  static formatToDateTimeLocal(date: any): string {
    if (date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');

      // Return the date formatted as "YYYY-MM-DDTHH:MM"
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    return date; // Return the input if the date is not valid
  }
}


export let months: { title: string, value: string }[] = [
  {title: 'January', value: 'january'},
  {title: 'February', value: 'february'},
  {title: 'March', value: 'march'},
  {title: 'April', value: 'april'},
  {title: 'May', value: 'may'},
  {title: 'June', value: 'june'},
  {title: 'July', value: 'july'},
  {title: 'August', value: 'august'},
  {title: 'September', value: 'september'},
  {title: 'October', value: 'october'},
  {title: 'November', value: 'november'},
  {title: 'December', value: 'december'},
]
